import {baseApi} from 'core/redux/store/api';
import {transformErrorResponse} from 'core/redux/store/api/utils';
import {
    ICreateCommentRequest,
    ICreateReviewRequest,
    IGetReviewsRequest,
    IGetReviewsTotalRequest,
    IVoteReviewRequest,
    TCreateCommentResponse,
    TCreateReviewResponse,
    TGetReviewsResponse,
    TGetReviewsTotalResponse,
    TGetReviewsTransformedResponse,
    TVoteReviewResponse,
} from '../models/Review';
import qs from 'qs';
import {API_VERSION} from 'core/helpers/env';
import {buildHeaders} from 'core/helpers/api/headers';
import {TProductResponse} from '../models/ProductResponse';

/* eslint-disable sort-keys */
export const productApiService = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getProduct: builder.query<TProductResponse, string>({
            query: (code) => {
                return {
                    url: `${API_VERSION}/catalog/products/${code}`,
                    method: 'GET',
                    headers: buildHeaders({withSeo: 'detail'}),
                };
            },
        }),
        getReviews: builder.query({
            query: (query: IGetReviewsRequest) => {
                const serializedQuery = qs.stringify(query, {
                    filter: (prefix, value) => {
                        if ('filter' === prefix && !value.rating) {
                            return {code: value.code};
                        }
                        return value;
                    },
                });

                return {
                    url: `${API_VERSION}/reviews?${serializedQuery}`,
                    method: 'GET',
                };
            },
            merge: (currentCache, newData, {arg}) => {
                const pageNumber = arg.pageNumber || 0;

                if (1 < pageNumber) {
                    currentCache.items = [...currentCache.items, ...newData.items];
                    currentCache.pagination = newData.pagination;
                    currentCache.seo = newData.seo;
                } else {
                    currentCache.items = newData.items;
                    currentCache.pagination = newData.pagination;
                    currentCache.seo = newData.seo;
                }
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return `${endpointName}${queryArgs.filter.code}${queryArgs.filter.rating}${queryArgs.pageSize}${queryArgs.sort}`;
            },
            forceRefetch: ({currentArg, previousArg}) => {
                const isFilterChanged =
                    currentArg?.filter?.code !== previousArg?.filter?.code ||
                    currentArg?.filter?.rating !== previousArg?.filter?.rating;
                const isSortChanged = currentArg?.sort !== previousArg?.sort;
                const isPageChanged = currentArg?.pageNumber !== previousArg?.pageNumber;
                const isPageSizeChanged = currentArg?.pageSize !== previousArg?.pageSize;
                return isPageChanged || isFilterChanged || isSortChanged || isPageSizeChanged;
            },
            transformResponse: (data: TGetReviewsResponse): TGetReviewsTransformedResponse => ({
                items: data.data.items,
                pagination: data.pagination,
                seo: data.seo,
            }),
            transformErrorResponse,
        }),
        getReviewsTotal: builder.query({
            query: (query: IGetReviewsTotalRequest) => {
                const serializedQuery = qs.stringify(query, {
                    filter: (prefix, value) => {
                        if ('filter' === prefix && !value.rating) {
                            return {code: value.code};
                        }
                        return value;
                    },
                });

                return {
                    url: `${API_VERSION}/reviews/total?${serializedQuery}`,
                    method: 'GET',
                };
            },
            transformResponse: (data: TGetReviewsTotalResponse): TGetReviewsTotalResponse['data'] => data?.data,
        }),
        createReview: builder.mutation<TCreateReviewResponse['data'], ICreateReviewRequest>({
            query: (query) => {
                const body = query;

                return {
                    url: `${API_VERSION}/reviews`,
                    method: 'POST',
                    body,
                };
            },
        }),
        createComment: builder.mutation<TCreateCommentResponse['data'], ICreateCommentRequest>({
            query: (query) => {
                const {reviewId, ...body} = query;

                return {
                    url: `${API_VERSION}/reviews/${reviewId}/comment`,
                    method: 'POST',
                    body,
                };
            },
        }),
        voteReview: builder.mutation<TVoteReviewResponse['data'], IVoteReviewRequest>({
            query: (query) => {
                const {reviewId, ...body} = query;

                return {
                    url: `${API_VERSION}/reviews/${reviewId}/vote`,
                    method: 'POST',
                    body,
                };
            },
            transformResponse: (data: TVoteReviewResponse) => data.data,
        }),
    }),
});

export const {
    useGetReviewsQuery,
    useGetReviewsTotalQuery,
    useGetProductQuery,
    useCreateReviewMutation,
    useCreateCommentMutation,
    useVoteReviewMutation,
    util: {getRunningQueriesThunk: getReviewsRunningQueriesThunk},
} = productApiService;

export const {getReviews, getReviewsTotal, getProduct} = productApiService.endpoints;
